import React from "react";

import { isModalDialogOpen } from "#app/lib/hooks/use-hotkey";
import { IS_MAC, shortcutLabel } from "#app/lib/platform";
import { isTextEditingTarget } from "#app/lib/utils/typing-target";

/** Keycap for the bare F that opens the current view's filter panel — the
 *  toolbar chip's tooltip advertises it while its panel is the target, and
 *  the shortcuts reference derives its row from it. */
export const FILTER_PANEL_SHORTCUT: readonly string[] = ["F"];

/** Keycaps for the chord that clears the current view's filters. The
 *  Option/Shift glyphs only read right on Mac; elsewhere the keys are spelled
 *  out. Both name `altKey` + `shiftKey`. */
export const CLEAR_FILTERS_SHORTCUT: readonly string[] = IS_MAC
  ? ["⌥", "⇧", "F"]
  : ["Alt", "Shift", "F"];

/** The clear chord as one run of text, for `MenuShortcut` slots. */
export const CLEAR_FILTERS_SHORTCUT_LABEL = shortcutLabel(CLEAR_FILTERS_SHORTCUT);

/** What the winning view's filter panel does when a chord lands. */
type FilterHotkeyHandlers = {
  /** The toolbar chip (or dock) the panel hangs off — the element that
   *  decides document order and dialog membership. */
  element: () => HTMLElement | null;
  /** Open the panel (the bare F). */
  open: () => void;
  /** Clear both modes' filters (⌥/Alt+⇧+F). */
  clearAll: () => void;
  /** Whether the view has anything to clear — the clear chord is inert
   *  otherwise. */
  hasActiveFilters: () => boolean;
};

type Registration = {
  handlers: React.RefObject<FilterHotkeyHandlers>;
};

// ONE owner for both chords, app-wide. Every mounted filter panel registers
// here; the one whose chip is furthest along in document order is the target.
// That order is the arbitration — same rule as the view search's `/`
// (use-slash-search.ts): a dialog's panel portals to the end of <body>, so it
// wins while the dialog is open and hands the keys back when it closes. No
// per-screen rules.
const registrations = new Set<Registration>();
const subscribers = new Set<() => void>();
let current: Registration | null = null;

function resolveCurrent(): Registration | null {
  let winner: Registration | null = null;
  let winnerElement: HTMLElement | null = null;
  for (const registration of registrations) {
    const element = registration.handlers.current.element();
    if (!element) continue;
    if (
      !winnerElement ||
      winnerElement.compareDocumentPosition(element) &
        Node.DOCUMENT_POSITION_FOLLOWING
    ) {
      winner = registration;
      winnerElement = element;
    }
  }
  return winner;
}

function refresh(): void {
  current = resolveCurrent();
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

/**
 * Which chord `e` is, if either. The bare F follows the viewer's layout
 * (`e.key` — the letter they see printed is the letter that works) and takes
 * no modifier. The clear chord gates on the physical key (`e.code`): Option
 * is a compose modifier, so macOS reports `e.key` as "Ï" and a key-based gate
 * silently never fires.
 */
function chordOf(e: KeyboardEvent): "open" | "clear" | null {
  if (e.metaKey || e.ctrlKey) return null;
  if (e.altKey || e.shiftKey) {
    return e.altKey && e.shiftKey && e.code === "KeyF" ? "clear" : null;
  }
  return e.key === "f" || e.key === "F" ? "open" : null;
}

function handleKeyDown(e: KeyboardEvent): void {
  const chord = chordOf(e);
  if (!chord) return;
  if (e.defaultPrevented) return;
  // Typing owns the key either way: bare F is a character, and ⌥⇧F is a
  // text-composing chord in an editor.
  if (isTextEditingTarget(e.target)) return;
  const origin = e.target instanceof Element ? e.target : null;
  // A menu, listbox or combobox owns a bare letter as typeahead. The clear
  // chord carries modifiers, so it stays live there — clearing while the
  // panel itself is open shows the values resetting.
  if (
    chord === "open" &&
    origin?.closest('[role="menu"],[role="listbox"],[role="combobox"]')
  ) {
    return;
  }

  // Resolved at press time, not from the cached `current`: a chip's element
  // can appear after its registration, and the cached value only feeds the
  // tooltips.
  const winner = resolveCurrent();
  const handlers = winner?.handlers.current ?? null;
  const winnerElement = handlers?.element() ?? null;
  if (!handlers || !winnerElement) return;

  // A dialog owns the keys while it is open, unless the target is the
  // dialog's own panel — the chords must never reach a panel behind a modal,
  // whether focus is inside the modal or fell to <body>.
  const winnerDialog = winnerElement.closest('[role="dialog"]');
  const originDialog = origin?.closest('[role="dialog"]') ?? null;
  if (originDialog && originDialog !== winnerDialog) return;
  if (!winnerDialog && isModalDialogOpen()) return;

  if (chord === "clear") {
    if (!handlers.hasActiveFilters()) return;
    e.preventDefault();
    handlers.clearAll();
    return;
  }
  e.preventDefault();
  handlers.open();
}

function register(registration: Registration): () => void {
  registrations.add(registration);
  if (registrations.size === 1) window.addEventListener("keydown", handleKeyDown);
  refresh();
  return () => {
    registrations.delete(registration);
    if (registrations.size === 0) {
      window.removeEventListener("keydown", handleKeyDown);
    }
    refresh();
  };
}

/**
 * Registers a filter panel as a place the F / ⌥⇧F chords can land while the
 * caller is mounted. Returns whether it is the CURRENT target — the panel the
 * chords would act on right now — so the caller advertises the keycaps only
 * when it is true. `handlers` is read through a ref, so a fresh object each
 * render is fine and never re-registers.
 */
export function useFilterHotkeyTarget(handlers: FilterHotkeyHandlers): boolean {
  const handlersRef = React.useRef(handlers);
  React.useEffect(() => {
    handlersRef.current = handlers;
  });

  const registration = React.useMemo<Registration>(
    () => ({ handlers: handlersRef }),
    [],
  );

  React.useEffect(() => register(registration), [registration]);

  return React.useSyncExternalStore(
    subscribe,
    () => current === registration,
    () => false,
  );
}
